import { useState, useEffect, useCallback } from 'react';
import { getCollectionService } from '../../infrastructure/services/collection.service';
import type { CollectionEntity } from '../../domain/entities/collection.entity';

interface NavCollection {
  label: string;
  slug: string;
  imageUri?: string;
}

const FALLBACK_NAV: NavCollection[] = [
  { label: 'Best Sellers', slug: 'best-sellers' },
  { label: 'Shapewear', slug: 'shapewear' },
  { label: 'Bras', slug: 'bras' },
  { label: 'Briefs & Panties', slug: 'briefs-panties' },
];

function toNavCollection(c: CollectionEntity): NavCollection {
  return { label: c.title, slug: c.slug, imageUri: c.imageUri };
}

interface UseNavCollectionsResult {
  collections: NavCollection[];
  loading: boolean;
}

let cachedCollections: NavCollection[] | null = null;
let fetchPromise: Promise<NavCollection[]> | null = null;

let cachedSaleCollection: { slug: string; title: string } | null = null;

/** Allows external callers (e.g. page-bundle hooks) to pre-populate the sale collection cache. */
export function setSaleCachedCollection(
  data: { slug: string; title: string } | null,
): void {
  if (data) cachedSaleCollection = data;
}

/** Returns the cached sale collection, kept in sync via page-bundle hooks. */
export function useSaleCollection(): { slug: string; title: string } | null {
  const [sale, setSale] = useState(cachedSaleCollection);
  useEffect(() => {
    if (cachedSaleCollection) setSale(cachedSaleCollection);
  }, []);
  return sale;
}

/** Returns true when menu collections have been loaded into the module cache. */
export function isNavCached(): boolean {
  return cachedCollections !== null;
}

/** Returns the cached sale collection value synchronously (for use outside React). */
export function getCachedSaleCollection(): { slug: string; title: string } | null {
  return cachedSaleCollection;
}

/** Allows external callers (e.g. page-bundle hooks) to pre-populate the cache. */
export function setCachedCollections(collections: NavCollection[]): void {
  cachedCollections = collections;
}

function fetchMenuCollections(): Promise<NavCollection[]> {
  if (cachedCollections) return Promise.resolve(cachedCollections);
  if (fetchPromise) return fetchPromise;

  fetchPromise = getCollectionService()
    .getMenuCollections()
    .then((items) => {
      const mapped = items.length > 0 ? items.map(toNavCollection) : FALLBACK_NAV;
      cachedCollections = mapped;
      return mapped;
    })
    .catch(() => {
      cachedCollections = FALLBACK_NAV;
      return FALLBACK_NAV;
    })
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

export function useNavCollections(): UseNavCollectionsResult {
  const [collections, setCollections] = useState<NavCollection[]>(
    cachedCollections ?? FALLBACK_NAV,
  );
  const [loading, setLoading] = useState(!cachedCollections);

  const load = useCallback(async () => {
    if (cachedCollections) {
      setCollections(cachedCollections);
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await fetchMenuCollections();
    setCollections(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { collections, loading };
}

export type { NavCollection };
